import React, { useEffect, useRef } from 'react';

export interface Position {
  id: number;
  exchange: 'HYPERLIQUID' | 'BINANCE' | 'BITGET';
  symbol: string;
  binance_symbol: string;
  direction: 'LONG' | 'SHORT';
  entry_price: number;
  entry_amount_usdt: number;
  remaining_quantity: number;
  sl_price: number | null;
  current_price: number | null;
  unrealized_pnl: number | null;
  unrealized_pnl_pct: number | null;
  opened_at: string;
}

interface Props {
  position: Position;
  hasApiKey: boolean;
  onClose: (pos: Position) => void;
  onEditSL: (pos: Position) => void;
}

function platformClass(exchange: string) {
  if (exchange === 'HYPERLIQUID') return 'hl';
  if (exchange === 'BINANCE') return 'bn';
  return 'bg';
}

function platformLabel(exchange: string) {
  if (exchange === 'HYPERLIQUID') return 'Hyperliquid';
  if (exchange === 'BINANCE') return 'Binance';
  return 'Bitget';
}

function fmtPrice(n: number | null) {
  if (n === null) return '—';
  if (n >= 1000) return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (n >= 1) return `$${n.toFixed(4)}`;
  return `$${n.toFixed(6)}`;
}

function fmtQty(n: number, symbol: string) {
  const decimals = n >= 1 ? 4 : 6;
  return `${n.toFixed(decimals)} ${symbol}`;
}

const PositionCard: React.FC<Props> = ({ position: pos, hasApiKey, onClose, onEditSL }) => {
  const pnlRef = useRef<HTMLDivElement>(null);
  const prevPnl = useRef<number | null>(pos.unrealized_pnl);

  useEffect(() => {
    if (prevPnl.current !== null && pos.unrealized_pnl !== null &&
        prevPnl.current !== pos.unrealized_pnl) {
      pnlRef.current?.classList.remove('pos-pnl-flash');
      void pnlRef.current?.offsetWidth;
      pnlRef.current?.classList.add('pos-pnl-flash');
    }
    prevPnl.current = pos.unrealized_pnl;
  }, [pos.unrealized_pnl]);

  const pnl = pos.unrealized_pnl;
  const pnlPct = pos.unrealized_pnl_pct;
  const pnlClass = pnl === null ? 'neutral' : pnl >= 0 ? 'green' : 'red';
  const pnlSign = pnl === null ? '' : pnl >= 0 ? '+' : '';
  const pnlPctSign = pnlPct === null ? '' : pnlPct >= 0 ? '▲ +' : '▼ ';
  const canTrade = pos.exchange === 'HYPERLIQUID' || hasApiKey;

  return (
    <div className="pos-card">
      <div className="pos-card-body">

        {/* Top */}
        <div className="pos-card-top">
          <div className="pos-card-meta">
            <span className={`pos-platform ${platformClass(pos.exchange)}`}>
              <span className="pos-platform-dot" />
              {platformLabel(pos.exchange)}
            </span>
            <span className="pos-symbol">{pos.symbol}</span>
            <span className={`pos-dir ${pos.direction.toLowerCase()}`}>{pos.direction}</span>
          </div>
          <div className="pos-pnl">
            <div ref={pnlRef} className={`pos-pnl-amount ${pnlClass}`}>
              {pnl === null ? '—' : `${pnlSign}$${Math.abs(pnl).toFixed(2)}`}
            </div>
            <div className={`pos-pnl-pct ${pnlClass}`}>
              {pnlPct === null ? '' : `${pnlPctSign}${Math.abs(pnlPct).toFixed(1)}%`}
            </div>
          </div>
        </div>

        {/* Metrics */}
        <div className="pos-metrics">
          <div className="pos-metric">
            <div className="pos-metric-label">Entry Price</div>
            <div className="pos-metric-val">{fmtPrice(pos.entry_price)}</div>
          </div>
          <div className="pos-metric">
            <div className="pos-metric-label">Current Price</div>
            <div className="pos-metric-val">{fmtPrice(pos.current_price)}</div>
          </div>
          <div className="pos-metric">
            <div className="pos-metric-label">Size</div>
            <div className="pos-metric-val">{fmtQty(pos.remaining_quantity, pos.symbol)}</div>
          </div>
          <div className="pos-metric">
            <div className="pos-metric-label">Stop Loss</div>
            <div className={`pos-metric-val ${pos.sl_price ? 'sl' : 'muted'}`}>
              {pos.sl_price ? fmtPrice(pos.sl_price) : '—'}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="pos-actions">
          {canTrade ? (
            <>
              <button className="pos-btn pos-btn-outline" onClick={() => onEditSL(pos)}>
                ✏️ Edit SL / TP
              </button>
              <button className="pos-btn pos-btn-danger" onClick={() => onClose(pos)}>
                Close Position
              </button>
            </>
          ) : (
            <>
              <button className="pos-btn pos-btn-disabled" disabled>✏️ Edit SL / TP</button>
              <button className="pos-btn pos-btn-disabled" disabled>Close Position</button>
            </>
          )}
        </div>

        {/* No API key warning */}
        {!canTrade && (
          <div className="pos-no-key">
            ⚠️ {platformLabel(pos.exchange)} API key not bound. Send <strong>/bindkey</strong> to the Galeon Telegram bot to enable trading actions.
          </div>
        )}

      </div>
    </div>
  );
};

export default PositionCard;
